import { useTranslation } from "react-i18next";
import { CButton, CFormSelect } from "@coreui/react-pro";
import CIcon from "@coreui/icons-react";
import { cilX } from "@coreui/icons";
import { offersForLine, type UsableOfferDto } from "../../../api/hooks/useOffers";
import { discountLabel } from "../../../lib/offer-copy";
import { savingOnLine } from "../../../lib/offer-savings";
import { formatPriceUsd } from "../../../lib/money";
import { useCartStore, type UpgradeLine } from "../../../state/cartStore";

interface UpgradeLineOfferProps {
  line: UpgradeLine;
  offers: UsableOfferDto[];
}

/**
 * The offer on an upgrade: what is on it, and the picker when there is a choice to make.
 *
 * Deliberately a copy of CartLineOffer without its offer cap handling — an upgrade is the only line in
 * the cart, so an offer never has to come off another one to go on it.
 */
export function UpgradeLineOffer({ line, offers }: UpgradeLineOfferProps) {
  const { t } = useTranslation("offers");
  const setUpgradeOffer = useCartStore((s) => s.setUpgradeOffer);

  const fitting = offersForLine(offers, line.strategyId, "Yearly");
  const chosen = fitting.find((offer) => offer.id === line.offerId) ?? null;

  if (fitting.length === 0) return null;

  if (chosen) {
    return (
      <div className="d-flex align-items-center gap-2 flex-wrap">
        <span className="badge rounded-pill text-bg-success-subtle border border-success px-3 py-2">
          <strong>{chosen.publicName}</strong> ·{" "}
          {t("cart.saves", { amount: formatPriceUsd(savingOnLine(chosen, line.yearlyPriceUsd)) })}
        </span>
        <CButton
          color="link"
          size="sm"
          className="p-0 text-body-secondary"
          onClick={() => setUpgradeOffer(null)}
          aria-label={t("cart.removeOffer", { offer: chosen.publicName })}
        >
          <CIcon icon={cilX} />
        </CButton>
        {fitting.length === 1 && (
          <span className="small text-body-secondary">{t("cart.onlyOfferThatFits")}</span>
        )}
      </div>
    );
  }

  return (
    <div className="d-flex align-items-center gap-2 flex-wrap">
      <span className="small text-body-secondary">{t("cart.offerLabel")}</span>
      <CFormSelect
        size="sm"
        className="w-auto mw-100"
        value=""
        aria-label={t("cart.chooseFor", { strategy: line.name })}
        onChange={(e) => setUpgradeOffer(e.target.value === "" ? null : e.target.value)}
      >
        <option value="">{t("cart.chooseCount", { count: fitting.length })}</option>
        {fitting.map((offer) => (
          <option key={offer.id} value={offer.id}>
            {t("cart.option", {
              name: offer.publicName,
              discount: discountLabel(offer, t),
              amount: formatPriceUsd(savingOnLine(offer, line.yearlyPriceUsd)),
            })}
          </option>
        ))}
      </CFormSelect>
    </div>
  );
}
